<template>
  <slot :isOpen="isOpen" :close="close" :open="open" :toggle="toggle" />

  <Teleport to="body">
    <Overlay v-if="showOverlay" v-show="isOpen" />
    <div
      role="dialog"
      aria-modal="true"
      :id="id"
      :aria-label="id"
      :class="className"
      ref="container-ref"
    >
      <slot
        name="content"
        :isOpen="isOpen"
        :close="close"
        :open="open"
        :toggle="toggle"
      />
    </div>
  </Teleport>
</template>

<script>
export default {
  name: "Modal",
};
</script>

<script setup>
import useCustomEventListener from "@/vue/composables/useCustomEventListener";
import Overlay from "../Overlay";
import {
  onUnmounted,
  ref,
  onMounted,
  toRefs,
  useTemplateRef,
  watchEffect,
} from "vue";
import {useFocusTrap} from "@/vue/composables/useFocusTrap";

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  className: String,

  showOverlay: {
    type: Boolean,
    default: true,
  },
  onOpen: {
    type: Function,
    required: false,
  },
  onClose: {
    type: Function,
    required: false,
  },
});

const {id, className, onOpen, onClose} = toRefs(props);

const isOpen = ref(false);
const interval = ref(null);

const container = useTemplateRef("container-ref");

const {setFocusTrap, removeFocusTrap} = useFocusTrap();

const open = e => {
  onOpen.value && onOpen.value();
  e?.stopImmediatePropagation && e.stopImmediatePropagation();
  isOpen.value = true;
  document.body.parentElement.style.overflowY = "hidden";
  document.addEventListener("keyup", handleEscapeKey);

  // Registered in a later task, never during the click that opened the modal.
  // `open` is reached from a click handler — a button, or a store action
  // dispatched by one — and that click is still on its way up to `document`.
  // Added synchronously, this listener caught that very click, saw a target
  // outside the dialog and closed it again, so the modal opened and shut
  // within the same click. The `stopImmediatePropagation` above only helps
  // when `open` is handed the event, which it is not when it comes from a
  // custom event.
  setTimeout(() => {
    // Closed again in the meantime: nothing to listen for.
    if (isOpen.value) document.addEventListener("click", handleClickOutside);
  }, 0);
  try {
    setFocusTrap(`#${id.value}`, {
      allowOutsideClick: true,
      checkCanFocusTrap: trapContainers => {
        const results = trapContainers.map(trapContainer => {
          return new Promise(resolve => {
            const canFocusTrap = () => {
              if (
                getComputedStyle(trapContainer.children[0]).visibility !==
                "hidden"
              ) {
                resolve();
              } else {
                requestAnimationFrame(canFocusTrap);
              }
            };

            requestAnimationFrame(canFocusTrap);
          });
        });
        return Promise.all(results);
      },
    });
  } catch (e) {
    console.error(e);
  }
};

const close = () => {
  try {
    removeFocusTrap();
  } catch (e) {
    console.error(e);
  }

  onClose.value && onClose.value();
  isOpen.value = false;
  document.body.parentElement.style.removeProperty("overflow-y");
  document.removeEventListener("keyup", handleEscapeKey);
  document.removeEventListener("click", handleClickOutside);
};

const toggle = e => (isOpen.value ? close(e) : open(e));

const handleEscapeKey = e => {
  if (e.key === "Escape") {
    close();
  }
};

const handleClickOutside = e => {
  if (!container.value) return;

  // `e.target` can be detached from the DOM before this document-level
  // listener runs: an async in-modal handler that flips a `v-if` (the discount
  // spinner) lets Vue flush the re-render in a microtask mid-propagation, and a
  // detached node is no longer `contains`-ed by the modal — which read as an
  // outside click and closed the drawer. `composedPath()` is captured when the
  // event is dispatched, so it still holds the original ancestor chain.
  const path = typeof e.composedPath === "function" ? e.composedPath() : null;
  const isInside = path
    ? path.includes(container.value)
    : container.value.contains(e.target);

  if (!isInside) {
    close();
  }
};

useCustomEventListener(`modal:${id.value}:open`, open);
useCustomEventListener(`modal:${id.value}:close`, close);
useCustomEventListener(`modal:${id.value}:toggle`, toggle);

onMounted(() => {
  document.addEventListener("keyup", handleEscapeKey);
  if (new URLSearchParams(location.search).get(id) === "true") {
    open();
  }
});

onUnmounted(() => {
  document.body.parentElement.style.removeProperty("overflow-y");
  clearInterval(interval.value);
  document.removeEventListener("keyup", handleEscapeKey);
  document.removeEventListener("click", handleClickOutside);
});

watchEffect(isOpen, () => {
  isOpen.value ? setFocusTrap() : removeFocusTrap();
});
</script>
